// @ts-nocheck
// app/admin/dashboard.tsx
import { useEffect, useMemo, useCallback, useState, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Pressable,
  ActivityIndicator,
  Platform,
  RefreshControl,
  TextInput,
  Alert,
} from 'react-native';
import { useRouter } from 'expo-router';
import {
  MessageSquare,
  BookOpen,
  Award,
  RefreshCw,
  Download,
  LogOut,
  Calendar,
  TrendingUp,
  TrendingDown,
  Filter,
  Search,
  Target,
  Zap,
  PieChart as PieChartIcon,
  BarChart3,
  Activity,
  ChevronRight,
  Users,
  MousePointerClick,
  Clock,
} from 'lucide-react-native';
import { supabase } from '@/utils/supabase';
import PieChart from '@/components/admin/PieChart';

// ═══════════════════════════════════════════════════════════
// 🎨 DESIGN SYSTEM - Monday.com Inspired
// ═══════════════════════════════════════════════════════════

const COLORS = {
  bg: '#F6F7FB',
  surface: '#FAF5EA',

  text: {
    primary: '#323338',
    secondary: '#676879',
    tertiary: '#9699A6',
    disabled: '#C5C7D0',
  },

  primary: '#0073EA',
  primaryHover: '#0060B9',
  primaryLight: '#CAE5FF',
  primaryBg: '#E5F4FF',

  success: '#00C875',
  successLight: '#D3F3E5',
  warning: '#FDAB3D',
  warningLight: '#FFF4E6',
  error: '#E44258',
  errorLight: '#FFE3E8',
  info: '#0086C0',

  chart: {
    blue: '#579BFC',
    purple: '#A25DDC',
    green: '#00C875',
    orange: '#FDAB3D',
    red: '#E44258',
    pink: '#FF5AC4',
    cyan: '#66CCFF',
    yellow: '#FFCB00',
    lime: '#9CD326',
    gray: '#C4C4C4',
  },

  border: '#E6E9EF',
  borderLight: '#F5F6F8',
  shadow: 'rgba(0, 0, 0, 0.08)',
};

const SPACING = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  xxl: 24,
  xxxl: 32,
  huge: 48,
};

const RADIUS = {
  xs: 4,
  sm: 6,
  md: 8,
  lg: 12,
  xl: 16,
  xxl: 20,
  full: 9999,
};

const TYPOGRAPHY = {
  display: {
    fontSize: 48,
    fontWeight: '600' as const,
    lineHeight: 56,
    color: COLORS.text.primary,
  },
  h1: {
    fontSize: 32,
    fontWeight: '600' as const,
    lineHeight: 40,
    color: COLORS.text.primary,
  },
  h2: {
    fontSize: 24,
    fontWeight: '500' as const,
    lineHeight: 32,
    color: COLORS.text.primary,
  },
  h3: {
    fontSize: 18,
    fontWeight: '500' as const,
    lineHeight: 24,
    color: COLORS.text.primary,
  },
  h4: {
    fontSize: 16,
    fontWeight: '500' as const,
    lineHeight: 22,
    color: COLORS.text.primary,
  },
  body: {
    fontSize: 14,
    fontWeight: '400' as const,
    lineHeight: 20,
    color: COLORS.text.secondary,
  },
  bodyMedium: {
    fontSize: 14,
    fontWeight: '500' as const,
    lineHeight: 20,
    color: COLORS.text.primary,
  },
  caption: {
    fontSize: 13,
    fontWeight: '400' as const,
    lineHeight: 18,
    color: COLORS.text.secondary,
  },
  small: {
    fontSize: 12,
    fontWeight: '400' as const,
    lineHeight: 16,
    color: COLORS.text.tertiary,
  },
};

const SHADOW = Platform.select({
  ios: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
  },
  android: {
    elevation: 3,
  },
  web: {
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  },
}) as any;

// ═══════════════════════════════════════════════════════════
// 🔧 TYPES & UTILITIES
// ═══════════════════════════════════════════════════════════

type RangeKey = 'today' | '7d' | '30d' | 'month' | 'custom';
type TopicCount = { topic: string; count: number };
interface FunnelStepData {
  step: string;
  count: number;
}
interface ButtonClickRow {
  button_name: string;
  page_path: string;
  click_count: number;
}
interface PageEngRow {
  page_path: string;
  unique_sessions: number;
  avg_scroll_pct: number;
  avg_time_seconds: number;
}

interface CollegeClickRow {
  college_name: string;
  click_count: number;
  last_clicked_at: string;
}

interface TopicClickRow {
  topic: string;
  click_count: number;
  last_clicked_at: string;
}

interface DashboardStats {
  sessionsAllTime: number;
  sessionsRange: number;
  sessionsPrev: number;
  todaySessions: number;
  weekSessions: number;
  monthSessions: number;
  selectionsRange: number;
  selectionsPrev: number;
  topTopics: TopicCount[];
  topicsSampleTotal: number;
  uniqueTopicsSample: number;
  coursesTotal: number;
  coursesWithVouchers: number;
  coursesWithoutVouchers: number;
  updatedAt: string | null;
}

type SortDir = 'asc' | 'desc';
type SortConfig<K extends string> = { key: K; dir: SortDir };

function clamp0(n: number) {
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

function calcTrendPct(current: number, prev: number) {
  if (!prev || prev <= 0) return 0;
  return Math.round(((current - prev) / prev) * 100);
}

function startOfDay(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function startOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function addDays(d: Date, days: number) {
  return new Date(d.getTime() + days * 24 * 60 * 60 * 1000);
}

function toIso(d: Date) {
  return d.toISOString();
}

function parseYMD(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const da = Number(m[3]);
  if (!y || mo < 1 || mo > 12 || da < 1 || da > 31) return null;
  const d = new Date(y, mo - 1, da);
  if (d.getFullYear() !== y || d.getMonth() !== mo - 1 || d.getDate() !== da) return null;
  return d;
}

function formatLabelFromDates(start: Date, end: Date) {
  try {
    const sTxt = start.toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit' });
    const eTxt = end.toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit' });
    return `${sTxt} - ${eTxt}`;
  } catch {
    return '—';
  }
}

function normalizeText(s: string) {
  return (s || '').toString().trim().toLowerCase();
}

function includesQuery(haystack: string, q: string) {
  if (!q) return true;
  return normalizeText(haystack).includes(q);
}

function sortNumber(a: number, b: number, dir: SortDir) {
  return dir === 'asc' ? a - b : b - a;
}

function sortString(a: string, b: string, dir: SortDir) {
  const aa = normalizeText(a);
  const bb = normalizeText(b);
  if (aa === bb) return 0;
  if (dir === 'asc') return aa < bb ? -1 : 1;
  return aa > bb ? -1 : 1;
}

async function safeCount(table: string): Promise<number> {
  try {
    const { count, error } = await supabase.from(table).select('*', { count: 'exact', head: true });
    if (error) return 0;
    return count ?? 0;
  } catch {
    return 0;
  }
}

async function safeCountInRange(table: string, tsCol: string, startISO: string, endISO: string): Promise<number> {
  try {
    const { count, error } = await supabase
      .from(table)
      .select('*', { count: 'exact', head: true })
      .gte(tsCol, startISO)
      .lt(tsCol, endISO);
    if (error) return 0;
    return count ?? 0;
  } catch {
    return 0;
  }
}

async function safeTopicsSampleInRange(startISO: string, endISO: string, limit = 5000): Promise<string[]> {
  try {
    const { data, error } = await supabase
      .from('course_selections')
      .select('topic')
      .not('topic', 'is', null)
      .gte('created_at', startISO)
      .lt('created_at', endISO)
      .limit(limit);

    if (error || !data) return [];
    return (data as any[])
      .map((x) => (typeof x?.topic === 'string' ? x.topic.trim() : ''))
      .filter(Boolean);
  } catch {
    return [];
  }
}

const FUNNEL_STEP_LABELS: Record<string, string> = {
  landing: 'נחיתה',
  topic_click: 'בחר נושא',
  chat_start: 'פתח שיחה',
  course_view: 'צפה בקורס',
  course_cta_click: 'לחץ CTA קורס',
  lead_modal_open: 'פתח טופס ליד',
  lead_submit: 'השאיר ליד',
  quiz_start: 'התחיל שאלון',
  quiz_complete: 'השלים שאלון',
};

const FUNNEL_STEPS_ORDER = [
  'landing',
  'topic_click',
  'chat_start',
  'course_view',
  'course_cta_click',
  'lead_modal_open',
  'lead_submit',
  'quiz_start',
  'quiz_complete',
];

async function safeFetchFunnelInRange(startISO: string, endISO: string): Promise<FunnelStepData[]> {
  try {
    const { data, error } = await supabase
      .from('funnel_events')
      .select('step, session_id')
      .gte('created_at', startISO)
      .lt('created_at', endISO)
      .limit(10000);
    if (error || !data) return [];

    const stepSessions: Record<string, Set<string>> = {};
    for (const row of data as any[]) {
      const s = row.step;
      if (!s) continue;
      if (!stepSessions[s]) stepSessions[s] = new Set();
      stepSessions[s].add(row.session_id);
    }

    return FUNNEL_STEPS_ORDER
      .filter((s) => stepSessions[s])
      .map((s) => ({ step: s, count: stepSessions[s].size }));
  } catch {
    return [];
  }
}

async function safeFetchTopButtonsInRange(startISO: string, endISO: string, limit = 10): Promise<ButtonClickRow[]> {
  try {
    const { data, error } = await supabase
      .from('button_clicks')
      .select('button_name, page_path')
      .gte('clicked_at', startISO)
      .lt('clicked_at', endISO)
      .limit(5000);
    if (error || !data) return [];

    const counts: Record<string, { count: number; page_path: string }> = {};
    for (const row of data as any[]) {
      const key = row.button_name;
      if (!key) continue;
      if (!counts[key]) counts[key] = { count: 0, page_path: row.page_path || '' };
      counts[key].count++;
    }

    return Object.entries(counts)
      .sort(([, a], [, b]) => b.count - a.count)
      .slice(0, limit)
      .map(([button_name, { count, page_path }]) => ({ button_name, page_path, click_count: count }));
  } catch {
    return [];
  }
}

async function safeFetchPageEngagementInRange(startISO: string, endISO: string, limit = 8): Promise<PageEngRow[]> {
  try {
    const { data, error } = await supabase
      .from('page_engagement')
      .select('page_path, session_id, scroll_depth_pct, time_on_page_seconds')
      .gte('created_at', startISO)
      .lt('created_at', endISO)
      .limit(5000);
    if (error || !data) return [];

    const paths: Record<
      string,
      { sessions: Set<string>; scrollSum: number; timeSum: number; count: number }
    > = {};
    for (const row of data as any[]) {
      const p = row.page_path || '—';
      if (!paths[p]) paths[p] = { sessions: new Set(), scrollSum: 0, timeSum: 0, count: 0 };
      paths[p].sessions.add(row.session_id);
      paths[p].scrollSum += row.scroll_depth_pct || 0;
      paths[p].timeSum += row.time_on_page_seconds || 0;
      paths[p].count++;
    }

    return Object.entries(paths)
      .sort(([, a], [, b]) => b.sessions.size - a.sessions.size)
      .slice(0, limit)
      .map(([page_path, d]) => ({
        page_path,
        unique_sessions: d.sessions.size,
        avg_scroll_pct: d.count > 0 ? Math.round(d.scrollSum / d.count) : 0,
        avg_time_seconds: d.count > 0 ? Math.round(d.timeSum / d.count) : 0,
      }));
  } catch {
    return [];
  }
}

async function safeFetchTopicClicksInRange(startISO: string, endISO: string): Promise<TopicClickRow[]> {
  try {
    const { data, error } = await supabase
      .from('topic_clicks')
      .select('topic, clicked_at')
      .gte('clicked_at', startISO)
      .lt('clicked_at', endISO)
      .limit(10000);
    if (error || !data) return [];

    const counts: Record<string, { count: number; last: string }> = {};
    for (const row of data as any[]) {
      const name = (row.topic || '').trim() || 'לא ידוע';
      if (!counts[name]) counts[name] = { count: 0, last: row.clicked_at };
      counts[name].count++;
      if (row.clicked_at > counts[name].last) counts[name].last = row.clicked_at;
    }

    return Object.entries(counts)
      .sort(([, a], [, b]) => b.count - a.count)
      .map(([topic, { count, last }]) => ({
        topic,
        click_count: count,
        last_clicked_at: last,
      }));
  } catch {
    return [];
  }
}

async function safeFetchCollegeClicksInRange(startISO: string, endISO: string): Promise<CollegeClickRow[]> {
  try {
    const { data, error } = await supabase
      .from('college_clicks')
      .select('college_name, clicked_at')
      .gte('clicked_at', startISO)
      .lt('clicked_at', endISO)
      .limit(5000);
    if (error || !data) return [];

    const counts: Record<string, { count: number; last: string }> = {};
    for (const row of data as any[]) {
      const name = row.college_name || 'לא ידוע';
      if (!counts[name]) counts[name] = { count: 0, last: row.clicked_at };
      counts[name].count++;
      if (row.clicked_at > counts[name].last) counts[name].last = row.clicked_at;
    }

    return Object.entries(counts)
      .sort(([, a], [, b]) => b.count - a.count)
      .map(([college_name, { count, last }]) => ({
        college_name,
        click_count: count,
        last_clicked_at: last,
      }));
  } catch {
    return [];
  }
}

async function safeCountLeadsInRange(startISO: string, endISO: string): Promise<number> {
  try {
    const { count, error } = await supabase
      .from('funnel_events')
      .select('*', { count: 'exact', head: true })
      .eq('step', 'lead_submit')
      .gte('created_at', startISO)
      .lt('created_at', endISO);
    if (error) return 0;
    return count ?? 0;
  } catch {
    return 0;
  }
}

// ═══════════════════════════════════════════════════════════
// 🎯 MAIN COMPONENT
// ═══════════════════════════════════════════════════════════

export default function AdminDashboard() {
  const router = useRouter();

  const [rangeKey, setRangeKey] = useState<RangeKey>('7d');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [loading, setLoading] = useState(true);
  const [authChecking, setAuthChecking] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({
    range: false,
    kpis: false,
    totals: false,
    charts: false,
    topics: false,
    funnel: false,
    buttons: false,
    engagement: false,
    college: false,
    topicClicks: false,
    actions: false,
  });

  const toggleSection = useCallback((key: string) => {
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  // ✅ Prevent stale async writes/navigation
  const runIdRef = useRef(0);

  const [stats, setStats] = useState<DashboardStats>({
    sessionsAllTime: 0,
    sessionsRange: 0,
    sessionsPrev: 0,
    todaySessions: 0,
    weekSessions: 0,
    monthSessions: 0,
    selectionsRange: 0,
    selectionsPrev: 0,
    topTopics: [],
    topicsSampleTotal: 0,
    uniqueTopicsSample: 0,
    coursesTotal: 0,
    coursesWithVouchers: 0,
    coursesWithoutVouchers: 0,
    updatedAt: null,
  });

  // ── New analytics state ──────────────────────────────────
  const [funnelData, setFunnelData] = useState<FunnelStepData[]>([]);
  const [topButtons, setTopButtons] = useState<ButtonClickRow[]>([]);
  const [pageEngData, setPageEngData] = useState<PageEngRow[]>([]);
  const [leadsRange, setLeadsRange] = useState<number>(0);
  const [collegeClicks, setCollegeClicks] = useState<CollegeClickRow[]>([]);
  const [topicClicksData, setTopicClicksData] = useState<TopicClickRow[]>([]);

  // ── Sorting state (feature) ──────────────────────────────
  const [sortTopicsCfg, setSortTopicsCfg] = useState<SortConfig<'topic' | 'count'>>({
    key: 'count',
    dir: 'desc',
  });
  const [sortFunnelCfg, setSortFunnelCfg] = useState<SortConfig<'step' | 'count'>>({
    key: 'count',
    dir: 'desc',
  });
  const [sortButtonsCfg, setSortButtonsCfg] = useState<
    SortConfig<'button_name' | 'page_path' | 'click_count'>
  >({ key: 'click_count', dir: 'desc' });
  const [sortEngCfg, setSortEngCfg] = useState<
    SortConfig<'page_path' | 'unique_sessions' | 'avg_scroll_pct' | 'avg_time_seconds'>
  >({ key: 'unique_sessions', dir: 'desc' });
  const [sortCollegeCfg, setSortCollegeCfg] = useState<SortConfig<'college_name' | 'click_count' | 'last_clicked_at'>>({
    key: 'click_count',
    dir: 'desc',
  });
  const [sortTopicClicksCfg, setSortTopicClicksCfg] = useState<SortConfig<'topic' | 'click_count' | 'last_clicked_at'>>({
    key: 'click_count',
    dir: 'desc',
  });

  const onToggleSort = useCallback(<K extends string>(cfg: SortConfig<K>, key: K, setFn: any) => {
    setFn((prev: SortConfig<K>) => {
      if (prev.key === key) return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      return { key, dir: cfg.dir };
    });
  }, []);

  // ── Persist settings (web only) ──────────────────────────
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    try {
      const w = typeof window !== 'undefined' ? window : null;
      if (!w?.localStorage) return;

      const savedRange = w.localStorage.getItem('kursi_admin_rangeKey') as RangeKey | null;
      const savedStart = w.localStorage.getItem('kursi_admin_customStart') || '';
      const savedEnd = w.localStorage.getItem('kursi_admin_customEnd') || '';

      if (savedRange && ['today', '7d', '30d', 'month', 'custom'].includes(savedRange)) {
        setRangeKey(savedRange);
      }
      if (savedStart) setCustomStart(savedStart);
      if (savedEnd) setCustomEnd(savedEnd);
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    try {
      const w = typeof window !== 'undefined' ? window : null;
      if (!w?.localStorage) return;

      w.localStorage.setItem('kursi_admin_rangeKey', rangeKey);
      w.localStorage.setItem('kursi_admin_customStart', customStart);
      w.localStorage.setItem('kursi_admin_customEnd', customEnd);
    } catch {}
  }, [rangeKey, customStart, customEnd]);

  const {
    startISO,
    endISO,
    prevStartISO,
    prevEndISO,
    rangeLabel,
    rangeError,
    customLabel,
  } = useMemo(() => {
    const now = new Date();
    const end = now;

    if (rangeKey === 'today') {
      const start = startOfDay(now);
      const s = toIso(start);
      const e = toIso(end);
      const dur = end.getTime() - start.getTime();
      const prevStart = new Date(start.getTime() - dur);
      const prevEnd = start;
      return {
        startISO: s,
        endISO: e,
        prevStartISO: toIso(prevStart),
        prevEndISO: toIso(prevEnd),
        rangeLabel: 'היום',
        customLabel: null as string | null,
        rangeError: null as string | null,
      };
    }

    if (rangeKey === '7d') {
      const start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const s = toIso(start);
      const e = toIso(end);
      const dur = end.getTime() - start.getTime();
      const prevStart = new Date(start.getTime() - dur);
      const prevEnd = start;
      return {
        startISO: s,
        endISO: e,
        prevStartISO: toIso(prevStart),
        prevEndISO: toIso(prevEnd),
        rangeLabel: '7 ימים',
        customLabel: null,
        rangeError: null,
      };
    }

    if (rangeKey === '30d') {
      const start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const s = toIso(start);
      const e = toIso(end);
      const dur = end.getTime() - start.getTime();
      const prevStart = new Date(start.getTime() - dur);
      const prevEnd = start;
      return {
        startISO: s,
        endISO: e,
        prevStartISO: toIso(prevStart),
        prevEndISO: toIso(prevEnd),
        rangeLabel: '30 ימים',
        customLabel: null,
        rangeError: null,
      };
    }

    if (rangeKey === 'month') {
      const start = startOfMonth(now);
      const s = toIso(start);
      const e = toIso(end);
      const dur = end.getTime() - start.getTime();
      const prevStart = new Date(start.getTime() - dur);
      const prevEnd = start;
      return {
        startISO: s,
        endISO: e,
        prevStartISO: toIso(prevStart),
        prevEndISO: toIso(prevEnd),
        rangeLabel: 'חודש נוכחי',
        customLabel: null,
        rangeError: null,
      };
    }

    // custom
    const sD = parseYMD(customStart);
    const eD = parseYMD(customEnd);

    if (!sD || !eD) {
      const start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const s = toIso(start);
      const e = toIso(end);
      const dur = end.getTime() - start.getTime();
      const prevStart = new Date(start.getTime() - dur);
      const prevEnd = start;
      return {
        startISO: s,
        endISO: e,
        prevStartISO: toIso(prevStart),
        prevEndISO: toIso(prevEnd),
        rangeLabel: 'טווח מותאם',
        customLabel: null,
        rangeError: 'טווח לא תקין (פורמט YYYY-MM-DD)',
      };
    }

    const start = startOfDay(sD);
    const endExclusive = startOfDay(addDays(eD, 1));

    if (endExclusive.getTime() <= start.getTime()) {
      const start7 = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const s = toIso(start7);
      const e = toIso(end);
      const dur = end.getTime() - start7.getTime();
      const prevStart = new Date(start7.getTime() - dur);
      const prevEnd = start7;
      return {
        startISO: s,
        endISO: e,
        prevStartISO: toIso(prevStart),
        prevEndISO: toIso(prevEnd),
        rangeLabel: 'טווח מותאם',
        customLabel: formatLabelFromDates(sD, eD),
        rangeError: 'תאריך הסיום חייב להיות אחרי ההתחלה',
      };
    }

    const s = toIso(start);
    const e = toIso(endExclusive);
    const dur = endExclusive.getTime() - start.getTime();
    const prevStart = new Date(start.getTime() - dur);
    const prevEnd = start;

    return {
      startISO: s,
      endISO: e,
      prevStartISO: toIso(prevStart),
      prevEndISO: toIso(prevEnd),
      rangeLabel: formatLabelFromDates(sD, eD),
      customLabel: formatLabelFromDates(sD, eD),
      rangeError: null,
    };
  }, [rangeKey, customStart, customEnd]);

  // ✅ P0: Guard admin access before loading stats
  const guardAdmin = useCallback(async (): Promise<boolean> => {
    try {
      setAuthChecking(true);

      const { data } = await supabase.auth.getSession();
      const session = data?.session;

      if (!session?.user?.id) {
        router.replace('/admin/login');
        return false;
      }

      const { data: adminRow, error: adminErr } = await supabase
        .from('admins')
        .select('id, is_active')
        .eq('user_id', session.user.id)
        .eq('is_active', true)
        .maybeSingle();

      if (adminErr || !adminRow) {
        await supabase.auth.signOut();
        router.replace('/admin/login');
        return false;
      }

      return true;
    } catch {
      try {
        await supabase.auth.signOut();
      } catch {}
      router.replace('/admin/login');
      return false;
    } finally {
      setAuthChecking(false);
    }
  }, [router]);

  const loadStats = useCallback(
    async (showRefresh = false) => {
      const runId = ++runIdRef.current;

      try {
        // ✅ Always enforce admin guard before every stats load
        const ok = await guardAdmin();
        if (!ok) return;

        if (runId !== runIdRef.current) return;

        if (showRefresh) setRefreshing(true);
        else setLoading(true);

        if (__DEV__ && rangeError) console.warn(rangeError);

        const now = new Date();
        const todayStart = toIso(startOfDay(now));
        const weekAgo = toIso(new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000));
        const monthAgo = toIso(new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000));
        const nowISO = toIso(new Date());

        const results = await Promise.allSettled([
          safeCount('chat_sessions'),
          safeCountInRange('chat_sessions', 'started_at', startISO, endISO),
          safeCountInRange('chat_sessions', 'started_at', prevStartISO, prevEndISO),
          safeCountInRange('chat_sessions', 'started_at', todayStart, nowISO),
          safeCountInRange('chat_sessions', 'started_at', weekAgo, nowISO),
          safeCountInRange('chat_sessions', 'started_at', monthAgo, nowISO),
          safeCountInRange('course_selections', 'created_at', startISO, endISO),
          safeCountInRange('course_selections', 'created_at', prevStartISO, prevEndISO),
          safeTopicsSampleInRange(startISO, endISO, 5000),
          safeCount('processed_raw_courses'),
          (async () => {
            try {
              const { count, error } = await supabase
                .from('processed_raw_courses')
                .select('*', { count: 'exact', head: true })
                .eq('has_voucher', true);
              if (error) return 0;
              return count ?? 0;
            } catch {
              return 0;
            }
          })(),
          (async () => {
            try {
              const { count, error } = await supabase
                .from('processed_raw_courses')
                .select('*', { count: 'exact', head: true })
                .eq('has_voucher', false);
              if (error) return 0;
              return count ?? 0;
            } catch {
              return 0;
            }
          })(),
        ]);

        if (runId !== runIdRef.current) return;

        const v = <T,>(i: number, fallback: T): T => {
          const r = results[i];
          return r && r.status === 'fulfilled' ? (r.value as T) : fallback;
        };

        const sessionsAllTime = v<number>(0, 0);
        const sessionsRange = v<number>(1, 0);
        const sessionsPrev = v<number>(2, 0);
        const todaySessions = v<number>(3, 0);
        const weekSessions = v<number>(4, 0);
        const monthSessions = v<number>(5, 0);
        const selectionsRange = v<number>(6, 0);
        const selectionsPrev = v<number>(7, 0);
        const topicsSample = v<string[]>(8, []);

        const topicsCount: Record<string, number> = {};
        for (const t of topicsSample) topicsCount[t] = (topicsCount[t] || 0) + 1;

        const topTopics: TopicCount[] = Object.entries(topicsCount)
          .sort(([, a], [, b]) => b - a)
          .slice(0, 5)
          .map(([topic, count]) => ({ topic, count }));

        const coursesTotal = v<number>(9, 0);
        const coursesWithVouchers = v<number>(10, 0);
        const coursesWithoutVouchers = v<number>(11, 0);

        setStats({
          sessionsAllTime: clamp0(sessionsAllTime),
          sessionsRange: clamp0(sessionsRange),
          sessionsPrev: clamp0(sessionsPrev),
          todaySessions: clamp0(todaySessions),
          weekSessions: clamp0(weekSessions),
          monthSessions: clamp0(monthSessions),
          selectionsRange: clamp0(selectionsRange),
          selectionsPrev: clamp0(selectionsPrev),
          topTopics,
          topicsSampleTotal: clamp0(topicsSample.length),
          uniqueTopicsSample: clamp0(Object.keys(topicsCount).length),
          coursesTotal: clamp0(coursesTotal),
          coursesWithVouchers: clamp0(coursesWithVouchers),
          coursesWithoutVouchers: clamp0(coursesWithoutVouchers),
          updatedAt: new Date().toISOString(),
        });

        // ── Load analytics tables ──
        const [funnelRes, buttonsRes, pageEngRes, leadsRes, collegeRes, topicClicksRes] = await Promise.allSettled([
          safeFetchFunnelInRange(startISO, endISO),
          safeFetchTopButtonsInRange(startISO, endISO),
          safeFetchPageEngagementInRange(startISO, endISO),
          safeCountLeadsInRange(startISO, endISO),
          safeFetchCollegeClicksInRange(startISO, endISO),
          safeFetchTopicClicksInRange(startISO, endISO),
        ]);

        if (runId === runIdRef.current) {
          setFunnelData(funnelRes.status === 'fulfilled' ? funnelRes.value : []);
          setTopButtons(buttonsRes.status === 'fulfilled' ? buttonsRes.value : []);
          setPageEngData(pageEngRes.status === 'fulfilled' ? pageEngRes.value : []);
          setLeadsRange(leadsRes.status === 'fulfilled' ? clamp0(leadsRes.value) : 0);
          setCollegeClicks(collegeRes.status === 'fulfilled' ? collegeRes.value : []);
          setTopicClicksData(topicClicksRes.status === 'fulfilled' ? topicClicksRes.value : []);
        }
      } catch (e) {
        console.error('Error loading stats:', e);
        Alert.alert('שגיאה', 'לא ניתן לטעון סטטיסטיקות');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [guardAdmin, startISO, endISO, prevStartISO, prevEndISO, rangeError]
  );

  // ✅ Initial load
  useEffect(() => {
    loadStats(false);
  }, [loadStats]);

  // ✅ Auto-load when switching non-custom ranges (QA fix: chips now actually reload data)
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    if (rangeKey !== 'custom') {
      loadStats(true);
    }
  }, [rangeKey, loadStats]);

  // ✅ If auth changes in another tab / session expires, kick out immediately
  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session?.user?.id) {
        router.replace('/admin/login');
      }
    });

    return () => {
      try {
        data?.subscription?.unsubscribe?.();
      } catch {}
    };
  }, [router]);

  const logout = async () => {
    try {
      await supabase.auth.signOut();
      router.replace('/admin/login');
    } catch (e) {
      console.error('Logout error:', e);
      Alert.alert('שגיאה', 'לא ניתן להתנתק');
    }
  };

  const updatedLabel = useMemo(() => {
    if (!stats.updatedAt) return '—';
    try {
      return new Date(stats.updatedAt).toLocaleString('he-IL', {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '—';
    }
  }, [stats.updatedAt]);

  const sessionsTrend = useMemo(
    () => calcTrendPct(stats.sessionsRange, stats.sessionsPrev),
    [stats.sessionsRange, stats.sessionsPrev]
  );

  const selectionsTrend = useMemo(
    () => calcTrendPct(stats.selectionsRange, stats.selectionsPrev),
    [stats.selectionsRange, stats.selectionsPrev]
  );

  const selectionsPerSession = useMemo(() => {
    if (!stats.sessionsRange) return 0;
    return Math.round((stats.selectionsRange / stats.sessionsRange) * 10) / 10;
  }, [stats.selectionsRange, stats.sessionsRange]);

  const conversionRate = useMemo(() => {
    if (!stats.sessionsRange) return 0;
    return Math.round((stats.selectionsRange / stats.sessionsRange) * 100);
  }, [stats.selectionsRange, stats.sessionsRange]);

  const leadConversionRate = useMemo(() => {
    if (!stats.sessionsRange) return 0;
    return Math.round((leadsRange / stats.sessionsRange) * 100);
  }, [leadsRange, stats.sessionsRange]);

  const leadsPerSession = useMemo(() => {
    if (!stats.sessionsRange) return 0;
    return Math.round((leadsRange / stats.sessionsRange) * 1000) / 1000;
  }, [leadsRange, stats.sessionsRange]);

  const voucherData = useMemo(
    () => [
      { label: 'עם הטבה', value: stats.coursesWithVouchers, color: COLORS.chart.green },
      { label: 'ללא הטבה', value: stats.coursesWithoutVouchers, color: COLORS.chart.gray },
    ],
    [stats.coursesWithVouchers, stats.coursesWithoutVouchers]
  );

  const topicsPie = useMemo(() => {
    const palette = [
      COLORS.chart.blue,
      COLORS.chart.purple,
      COLORS.chart.green,
      COLORS.chart.orange,
      COLORS.chart.pink,
    ];
    const top = stats.topTopics.map((t, i) => ({
      label: t.topic,
      value: t.count,
      color: palette[i % palette.length],
    }));

    const sumTop = stats.topTopics.reduce((s, t) => s + t.count, 0);
    const other = clamp0(stats.topicsSampleTotal - sumTop);
    if (other > 0) top.push({ label: 'אחר', value: other, color: COLORS.chart.gray });

    return top;
  }, [stats.topTopics, stats.topicsSampleTotal]);

  const q = useMemo(() => normalizeText(searchQuery), [searchQuery]);

  // ── Filtered + sorted data (search works) ────────────────
  const filteredTopics = useMemo(() => {
    const base = stats.topTopics.filter((t) => includesQuery(t.topic, q));
    const sorted = [...base].sort((a, b) => {
      if (sortTopicsCfg.key === 'topic') return sortString(a.topic, b.topic, sortTopicsCfg.dir);
      return sortNumber(a.count, b.count, sortTopicsCfg.dir);
    });
    return sorted;
  }, [stats.topTopics, q, sortTopicsCfg]);

  const filteredFunnel = useMemo(() => {
    const base = funnelData.filter((f) => {
      const label = FUNNEL_STEP_LABELS[f.step] || f.step;
      return includesQuery(label, q) || includesQuery(f.step, q);
    });
    const sorted = [...base].sort((a, b) => {
      if (sortFunnelCfg.key === 'step') {
        const la = FUNNEL_STEP_LABELS[a.step] || a.step;
        const lb = FUNNEL_STEP_LABELS[b.step] || b.step;
        return sortString(la, lb, sortFunnelCfg.dir);
      }
      return sortNumber(a.count, b.count, sortFunnelCfg.dir);
    });
    return sorted;
  }, [funnelData, q, sortFunnelCfg]);

  const filteredButtons = useMemo(() => {
    const base = topButtons.filter(
      (b) =>
        includesQuery(b.button_name, q) ||
        includesQuery(b.page_path || '', q) ||
        includesQuery(String(b.click_count), q)
    );
    const sorted = [...base].sort((a, b) => {
      if (sortButtonsCfg.key === 'button_name') return sortString(a.button_name, b.button_name, sortButtonsCfg.dir);
      if (sortButtonsCfg.key === 'page_path') return sortString(a.page_path || '', b.page_path || '', sortButtonsCfg.dir);
      return sortNumber(a.click_count, b.click_count, sortButtonsCfg.dir);
    });
    return sorted;
  }, [topButtons, q, sortButtonsCfg]);

  const filteredEng = useMemo(() => {
    const base = pageEngData.filter(
      (r) =>
        includesQuery(r.page_path, q) ||
        includesQuery(String(r.unique_sessions), q) ||
        includesQuery(String(r.avg_scroll_pct), q)
    );
    const sorted = [...base].sort((a, b) => {
      if (sortEngCfg.key === 'page_path') return sortString(a.page_path, b.page_path, sortEngCfg.dir);
      if (sortEngCfg.key === 'unique_sessions') return sortNumber(a.unique_sessions, b.unique_sessions, sortEngCfg.dir);
      if (sortEngCfg.key === 'avg_scroll_pct') return sortNumber(a.avg_scroll_pct, b.avg_scroll_pct, sortEngCfg.dir);
      return sortNumber(a.avg_time_seconds, b.avg_time_seconds, sortEngCfg.dir);
    });
    return sorted;
  }, [pageEngData, q, sortEngCfg]);

  const filteredCollegeClicks = useMemo(() => {
    const base = collegeClicks.filter((r) => includesQuery(r.college_name, q));
    return [...base].sort((a, b) => {
      if (sortCollegeCfg.key === 'college_name') return sortString(a.college_name, b.college_name, sortCollegeCfg.dir);
      if (sortCollegeCfg.key === 'last_clicked_at') return sortString(a.last_clicked_at, b.last_clicked_at, sortCollegeCfg.dir);
      return sortNumber(a.click_count, b.click_count, sortCollegeCfg.dir);
    });
  }, [collegeClicks, q, sortCollegeCfg]);

  const filteredTopicClicksData = useMemo(() => {
    const base = topicClicksData.filter((r) => includesQuery(r.topic, q));
    return [...base].sort((a, b) => {
      if (sortTopicClicksCfg.key === 'topic') return sortString(a.topic, b.topic, sortTopicClicksCfg.dir);
      if (sortTopicClicksCfg.key === 'last_clicked_at') return sortString(a.last_clicked_at, b.last_clicked_at, sortTopicClicksCfg.dir);
      return sortNumber(a.click_count, b.click_count, sortTopicClicksCfg.dir);
    });
  }, [topicClicksData, q, sortTopicClicksCfg]);

  const copyText = useCallback(async (text: string) => {
    if (!text) return;
    if (Platform.OS === 'web') {
      try {
        const nav = typeof navigator !== 'undefined' ? navigator : null;
        if (nav?.clipboard?.writeText) {
          await nav.clipboard.writeText(text);
          Alert.alert('הועתק', text);
          return;
        }
      } catch {}
    }
    Alert.alert('מידע', text);
  }, []);

  const RangeChip = ({ k, label }: { k: RangeKey; label: string }) => {
    const active = rangeKey === k;
    return (
      <Pressable
        onPress={() => setRangeKey(k)}
        style={({ pressed }) => [styles.chip, active && styles.chipActive, pressed && { opacity: 0.7 }]}
      >
        <Text style={[styles.chipText, active && styles.chipTextActive]}>{label}</Text>
      </Pressable>
    );
  };

  const SectionHeader = ({
    title,
    icon,
    sectionKey,
    desc,
  }: {
    title: string;
    icon: React.ReactNode;
    sectionKey: string;
    desc?: string;
  }) => {
    const isClosed = !!collapsed[sectionKey];
    return (
      <Pressable onPress={() => toggleSection(sectionKey)} style={({ pressed }) => [styles.sectionHeaderRow, pressed && { opacity: 0.95 }]}>
        <View style={styles.sectionHeaderLeft}>
          {icon}
          <View style={{ gap: 2 }}>
            <Text style={styles.sectionTitle}>{title}</Text>
            {!!desc && <Text style={styles.sectionDescInline}>{desc}</Text>}
          </View>
        </View>
        <View style={[styles.collapsePill, isClosed && styles.collapsePillClosed]}>
          <Text style={[styles.collapsePillText, isClosed && styles.collapsePillTextClosed]}>
            {isClosed ? 'פתח' : 'סגור'}
          </Text>
        </View>
      </Pressable>
    );
  };

  const SortHeaderCell = ({
    flex,
    label,
    active,
    dir,
    onPress,
    alignRight,
  }: {
    flex: number;
    label: string;
    active: boolean;
    dir: SortDir;
    onPress: () => void;
    alignRight?: boolean;
  }) => (
    <Pressable onPress={onPress} style={[styles.tableHeaderCell, { flex }, alignRight && { justifyContent: 'flex-end' }]}>
      <Text style={[styles.tableHeaderText, alignRight && { textAlign: 'right' }]}>
        {label}
        {active ? ` ${dir === 'asc' ? '↑' : '↓'}` : ''}
      </Text>
    </Pressable>
  );

  if (loading || authChecking) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={COLORS.primary} />
        <Text style={styles.loadingText}>{authChecking ? 'מאמת הרשאות...' : 'טוען נתונים...'}</Text>
      </View>
    );
  }

  return (
    <View style={styles.page}>
      <View style={styles.navbar}>
        <View style={styles.navContent}>
          <View style={styles.navLeft}>
            <Pressable onPress={logout} style={({ pressed }) => [styles.iconBtn, pressed && { opacity: 0.7 }]}>
              <LogOut size={18} color={COLORS.error} strokeWidth={2} />
            </Pressable>

            <Pressable onPress={() => router.push('/admin')} style={({ pressed }) => [styles.primaryBtn, pressed && { opacity: 0.9 }]}>
              <Text style={styles.primaryBtnText}>רשימות</Text>
              <ChevronRight size={14} color="#FFF" strokeWidth={2.5} />
            </Pressable>

            <Pressable onPress={() => router.push('/admin/export')} style={({ pressed }) => [styles.iconBtn, pressed && { opacity: 0.7 }]}>
              <Download size={18} color={COLORS.text.secondary} strokeWidth={2} />
            </Pressable>

            <Pressable onPress={() => loadStats(true)} style={({ pressed }) => [styles.iconBtn, pressed && { opacity: 0.7 }]}>
              <RefreshCw size={18} color={COLORS.text.secondary} strokeWidth={2} />
            </Pressable>
          </View>

          <View style={styles.searchBox}>
            <Search size={16} color={COLORS.text.tertiary} strokeWidth={2} />
            <TextInput
              value={searchQuery}
              onChangeText={setSearchQuery}
              placeholder="חיפוש (מסנן טבלאות)..."
              placeholderTextColor={COLORS.text.tertiary}
              style={styles.searchInput}
            />
            {!!searchQuery && (
              <Pressable onPress={() => setSearchQuery('')} style={({ pressed }) => [styles.clearBtn, pressed && { opacity: 0.8 }]}>
                <Text style={styles.clearBtnText}>×</Text>
              </Pressable>
            )}
          </View>

          <View style={styles.navRight}>
            <View style={styles.logoBox}>
              <Activity size={18} color={COLORS.primary} strokeWidth={2.5} />
            </View>
            <View>
              <Text style={styles.navTitle}>Kursi Analytics</Text>
              <Text style={styles.navSubtitle}>ניתוח והפקת תובנות</Text>
            </View>
          </View>
        </View>
      </View>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => loadStats(true)} tintColor={COLORS.primary} />}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.container}>
          <View style={styles.statusBar}>
            <View style={styles.dateRangeBadge}>
              <Calendar size={13} color={COLORS.primary} strokeWidth={2} />
              <Text style={styles.dateRangeText}>{rangeLabel}</Text>
            </View>

            <View style={styles.statusRight}>
              <Text style={styles.statusLabel}>עדכון:</Text>
              <Text style={styles.statusTime}>{updatedLabel}</Text>
              <View style={styles.liveBadge}>
                <View style={styles.liveDot} />
                <Text style={styles.liveText}>Live</Text>
              </View>
            </View>
          </View>

          {!!rangeError && rangeKey === 'custom' && (
            <View style={styles.errorBanner}>
              <Text style={styles.errorBannerTitle}>שגיאת טווח</Text>
              <Text style={styles.errorBannerText}>{rangeError}</Text>
              {!!customLabel && <Text style={styles.errorBannerHint}>טווח שהוקלד: {customLabel}</Text>}
            </View>
          )}

          <View style={styles.quickStats}>
            <View style={styles.quickCard}>
              <View style={styles.quickCardContent}>
                <Text style={styles.quickLabel}>שיחות פעילות</Text>
                <Text style={styles.quickValue}>{stats.sessionsRange}</Text>
                {sessionsTrend !== 0 && (
                  <View style={styles.quickTrend}>
                    {sessionsTrend > 0 ? (
                      <TrendingUp size={11} color={COLORS.success} strokeWidth={2.5} />
                    ) : (
                      <TrendingDown size={11} color={COLORS.error} strokeWidth={2.5} />
                    )}
                    <Text style={[styles.quickTrendText, { color: sessionsTrend > 0 ? COLORS.success : COLORS.error }]}>
                      {sessionsTrend > 0 ? '+' : ''}
                      {sessionsTrend}%
                    </Text>
                  </View>
                )}
              </View>
              <View style={[styles.quickIcon, { backgroundColor: COLORS.primaryBg }]}>
                <MessageSquare size={20} color={COLORS.primary} strokeWidth={2} />
              </View>
            </View>

            <View style={styles.quickCard}>
              <View style={styles.quickCardContent}>
                <Text style={styles.quickLabel}>שיעור המרה (בחירות)</Text>
                <Text style={styles.quickValue}>{conversionRate}%</Text>
                <View style={styles.miniProgress}>
                  <View style={[styles.miniProgressFill, { width: `${Math.min(conversionRate, 100)}%` }]} />
                </View>
              </View>
              <View style={[styles.quickIcon, { backgroundColor: '#F3F0FF' }]}>
                <Target size={20} color={COLORS.chart.purple} strokeWidth={2} />
              </View>
            </View>

            <View style={styles.quickCard}>
              <View style={styles.quickCardContent}>
                <Text style={styles.quickLabel}>בחירות קורסים</Text>
                <Text style={styles.quickValue}>{stats.selectionsRange}</Text>
                {selectionsTrend !== 0 && (
                  <View style={styles.quickTrend}>
                    {selectionsTrend > 0 ? (
                      <TrendingUp size={11} color={COLORS.success} strokeWidth={2.5} />
                    ) : (
                      <TrendingDown size={11} color={COLORS.error} strokeWidth={2.5} />
                    )}
                    <Text style={[styles.quickTrendText, { color: selectionsTrend > 0 ? COLORS.success : COLORS.error }]}>
                      {selectionsTrend > 0 ? '+' : ''}
                      {selectionsTrend}%
                    </Text>
                  </View>
                )}
              </View>
              <View style={[styles.quickIcon, { backgroundColor: COLORS.successLight }]}>
                <BookOpen size={20} color={COLORS.chart.green} strokeWidth={2} />
              </View>
            </View>

            <View style={styles.quickCard}>
              <View style={styles.quickCardContent}>
                <Text style={styles.quickLabel}>נושאים ייחודיים</Text>
                <Text style={styles.quickValue}>{stats.uniqueTopicsSample}</Text>
              </View>
              <View style={[styles.quickIcon, { backgroundColor: COLORS.warningLight }]}>
                <Award size={20} color={COLORS.chart.orange} strokeWidth={2} />
              </View>
            </View>

            <View style={styles.quickCard}>
              <View style={styles.quickCardContent}>
                <Text style={styles.quickLabel}>לידים (ווריד)</Text>
                <Text style={styles.quickValue}>{leadsRange}</Text>
                <Text style={styles.quickSub}>{leadConversionRate}% מהשיחות • {leadsPerSession} ליד/שיחה</Text>
              </View>
              <View style={[styles.quickIcon, { backgroundColor: COLORS.successLight }]}>
                <Users size={20} color={COLORS.chart.green} strokeWidth={2} />
              </View>
            </View>
          </View>

          {/* ─── טווח זמן ───────────────────────────────────── */}
          <View style={styles.section}>
            <SectionHeader
              title="טווח זמן לניתוח"
              desc="בחירה כאן מרעננת נתונים (לא צריך ללחוץ Refresh)"
              sectionKey="range"
              icon={<Filter size={15} color={COLORS.primary} strokeWidth={2} />}
            />

            {!collapsed.range && (
              <View style={styles.card}>
                <View style={styles.chipsRow}>
                  <RangeChip k="custom" label="טווח מותאם" />
                  <RangeChip k="month" label="חודש נוכחי" />
                  <RangeChip k="30d" label="30 ימים" />
                  <RangeChip k="7d" label="7 ימים" />
                  <RangeChip k="today" label="היום" />
                </View>

                {rangeKey === 'custom' && (
                  <View style={styles.customDateBox}>
                    <View style={styles.dateRow}>
                      <View style={styles.dateField}>
                        <Text style={styles.fieldLabel}>מתאריך</Text>
                        <TextInput
                          value={customStart}
                          onChangeText={setCustomStart}
                          placeholder="2026-01-01"
                          placeholderTextColor={COLORS.text.disabled}
                          style={styles.dateInput}
                        />
                      </View>

                      <View style={styles.dateField}>
                        <Text style={styles.fieldLabel}>עד תאריך</Text>
                        <TextInput
                          value={customEnd}
                          onChangeText={setCustomEnd}
                          placeholder="2026-12-31"
                          placeholderTextColor={COLORS.text.disabled}
                          style={styles.dateInput}
                        />
                      </View>

                      <Pressable onPress={() => loadStats(true)} style={({ pressed }) => [styles.applyBtn, pressed && { opacity: 0.9 }]}>
                        <Text style={styles.applyBtnText}>החל טווח</Text>
                      </Pressable>
                    </View>

                    {!!rangeError && (
                      <View style={styles.inlineHintBox}>
                        <Text style={styles.inlineHintText}>{rangeError}</Text>
                      </View>
                    )}
                  </View>
                )}
              </View>
            )}
          </View>

          {/* ─── KPI ────────────────────────────────────────── */}
          <View style={styles.section}>
            <SectionHeader
              title="מדדי ביצוע עיקריים"
              desc="מגמות בטווח הנבחר"
              sectionKey="kpis"
              icon={<BarChart3 size={15} color={COLORS.primary} strokeWidth={2} />}
            />

            {!collapsed.kpis && (
              <View style={styles.metricsGrid}>
                <View style={styles.metricCard}>
                  <View style={styles.metricHeader}>
                    <View style={[styles.metricIconBox, { backgroundColor: '#F3F0FF' }]}>
                      <Target size={18} color={COLORS.chart.purple} strokeWidth={2} />
                    </View>
                    <View>
                      <Text style={styles.metricTitle}>שיעור המרה</Text>
                      <Text style={styles.metricSubtitle}>בחירות לכל שיחה</Text>
                    </View>
                  </View>

                  <Text style={styles.metricValue}>{conversionRate}%</Text>

                  <View style={styles.metricFooter}>
                    <View style={styles.progressBar}>
                      <View style={[styles.progressFill, { width: `${Math.min(conversionRate, 100)}%` }]} />
                    </View>
                    <Text style={styles.metricNote}>ממוצע: {selectionsPerSession} בחירות</Text>
                  </View>
                </View>

                <View style={styles.metricCard}>
                  <View style={styles.metricHeader}>
                    <View style={[styles.metricIconBox, { backgroundColor: COLORS.successLight }]}>
                      <Users size={18} color={COLORS.chart.green} strokeWidth={2} />
                    </View>
                    <View>
                      <Text style={styles.metricTitle}>המרה ללידים</Text>
                      <Text style={styles.metricSubtitle}>לידים לכל שיחה</Text>
                    </View>
                  </View>

                  <Text style={styles.metricValue}>{leadConversionRate}%</Text>

                  <View style={styles.metricFooter}>
                    <View style={styles.progressBar}>
                      <View style={[styles.progressFill, { width: `${Math.min(leadConversionRate, 100)}%`, backgroundColor: COLORS.chart.green }]} />
                    </View>
                    <Text style={styles.metricNote}>יחס: {leadsPerSession} ליד/שיחה</Text>
                  </View>
                </View>

                <View style={styles.metricCard}>
                  <View style={styles.metricHeader}>
                    <View style={[styles.metricIconBox, { backgroundColor: COLORS.primaryBg }]}>
                      <MessageSquare size={18} color={COLORS.primary} strokeWidth={2} />
                    </View>
                    <View>
                      <Text style={styles.metricTitle}>שיחות פעילות</Text>
                      <Text style={styles.metricSubtitle}>בטווח הנבחר</Text>
                    </View>
                  </View>

                  <Text style={styles.metricValue}>{stats.sessionsRange}</Text>

                  {sessionsTrend !== 0 && (
                    <View style={styles.metricFooter}>
                      <View
                        style={[
                          styles.trendBadge,
                          { backgroundColor: sessionsTrend > 0 ? COLORS.successLight : COLORS.errorLight },
                        ]}
                      >
                        {sessionsTrend > 0 ? (
                          <TrendingUp size={13} color={COLORS.success} strokeWidth={2.5} />
                        ) : (
                          <TrendingDown size={13} color={COLORS.error} strokeWidth={2.5} />
                        )}
                        <Text style={[styles.trendText, { color: sessionsTrend > 0 ? COLORS.success : COLORS.error }]}>
                          {sessionsTrend > 0 ? '+' : ''}
                          {sessionsTrend}%
                        </Text>
                      </View>
                      <Text style={styles.metricNote}>לעומת תקופה קודמת</Text>
                    </View>
                  )}
                </View>
              </View>
            )}
          </View>

          {/* ─── Total sessions ─────────────────────────────── */}
          <View style={styles.section}>
            <SectionHeader
              title='סה"כ שיחות'
              desc="מתחילת הפעילות"
              sectionKey="totals"
              icon={<Zap size={15} color={COLORS.primary} strokeWidth={2} />}
            />

            {!collapsed.totals && (
              <View style={styles.totalCard}>
                <View style={[styles.totalIcon, { backgroundColor: COLORS.warningLight }]}>
                  <Zap size={22} color={COLORS.chart.orange} strokeWidth={2} />
                </View>

                <Text style={styles.totalValue}>{stats.sessionsAllTime}</Text>

                <View style={styles.totalBreakdown}>
                  <View style={styles.breakdownItem}>
                    <Text style={styles.breakdownValue}>{stats.todaySessions}</Text>
                    <Text style={styles.breakdownLabel}>היום</Text>
                  </View>
                  <View style={styles.breakdownDivider} />
                  <View style={styles.breakdownItem}>
                    <Text style={styles.breakdownValue}>{stats.weekSessions}</Text>
                    <Text style={styles.breakdownLabel}>שבוע</Text>
                  </View>
                  <View style={styles.breakdownDivider} />
                  <View style={styles.breakdownItem}>
                    <Text style={styles.breakdownValue}>{stats.monthSessions}</Text>
                    <Text style={styles.breakdownLabel}>חודש</Text>
                  </View>
                </View>
              </View>
            )}
          </View>

          {/* ─── Charts ─────────────────────────────────────── */}
          <View style={styles.section}>
            <SectionHeader
              title="גרפים"
              desc="נושאים והטבות"
              sectionKey="charts"
              icon={<PieChartIcon size={15} color={COLORS.primary} strokeWidth={2} />}
            />

            {!collapsed.charts && (
              <View style={styles.chartsRow}>
                <View style={styles.chartCard}>
                  <View style={styles.chartHeader}>
                    <View style={[styles.chartIconBox, { backgroundColor: COLORS.primaryBg }]}>
                      <Award size={16} color={COLORS.primary} strokeWidth={2} />
                    </View>
                    <View>
                      <Text style={styles.chartTitle}>נושאים פופולריים</Text>
                      <Text style={styles.chartSubtitle}>{stats.topicsSampleTotal} בחירות בדגימה</Text>
                    </View>
                  </View>
                  <View style={styles.chartBody}>
                    <PieChart data={topicsPie} size={170} title="" />
                  </View>
                </View>

                <View style={styles.chartCard}>
                  <View style={styles.chartHeader}>
                    <View style={[styles.chartIconBox, { backgroundColor: '#E0F2FE' }]}>
                      <PieChartIcon size={16} color={COLORS.info} strokeWidth={2} />
                    </View>
                    <View>
                      <Text style={styles.chartTitle}>התפלגות הטבות</Text>
                      <Text style={styles.chartSubtitle}>{stats.coursesTotal.toLocaleString()} קורסים במאגר</Text>
                    </View>
                  </View>
                  <View style={styles.chartBody}>
                    <PieChart data={voucherData} size={170} title="" />
                  </View>
                </View>
              </View>
            )}
          </View>

          {/* ─── Top topics ─────────────────────────────────── */}
          <View style={styles.section}>
            <SectionHeader
              title="נושאים מובילים"
              desc='Top 5 • חיפוש למעלה מסנן'
              sectionKey="topics"
              icon={<Award size={15} color={COLORS.primary} strokeWidth={2} />}
            />

            {!collapsed.topics && (
              <View style={styles.tableCard}>
                {filteredTopics.length === 0 ? (
                  <View style={styles.emptyState}>
                    <Text style={styles.emptyText}>אין מספיק נתונים בטווח/בחיפוש הנבחר</Text>
                  </View>
                ) : (
                  <View style={styles.table}>
                    <View style={styles.tableHeader}>
                      <SortHeaderCell
                        flex={0.5}
                        label="#נושא"
                        active={sortTopicsCfg.key === 'topic'}
                        dir={sortTopicsCfg.dir}
                        onPress={() => setSortTopicsCfg((p) => ({ key: 'topic', dir: p.key === 'topic' ? (p.dir === 'asc' ? 'desc' : 'asc') : 'asc' }))}
                      />
                      <SortHeaderCell
                        flex={1}
                        label="בחירות"
                        active={sortTopicsCfg.key === 'count'}
                        dir={sortTopicsCfg.dir}
                        onPress={() => setSortTopicsCfg((p) => ({ key: 'count', dir: p.key === 'count' ? (p.dir === 'asc' ? 'desc' : 'asc') : 'desc' }))}
                        alignRight
                      />
                      <View style={[styles.tableHeaderCell, { flex: 2 }]} />
                    </View>

                    {filteredTopics.map((topic, idx) => {
                      const max = filteredTopics[0]?.count || 1;
                      const pct = Math.round((topic.count / max) * 100);
                      const colors = [COLORS.chart.blue, COLORS.chart.purple, COLORS.chart.green, COLORS.chart.orange, COLORS.chart.pink];
                      const color = colors[idx % colors.length];

                      return (
                        <View key={`${topic.topic}_${idx}`} style={styles.tableRow}>
                          <View style={[styles.tableCell, { flex: 0.5 }]}>
                            <View style={[styles.rankBadge, { backgroundColor: color + '20' }]}>
                              <Text style={[styles.rankNum, { color }]}>{idx + 1}</Text>
                            </View>
                            <Text style={styles.topicText} numberOfLines={1}>
                              {topic.topic}
                            </Text>
                          </View>

                          <View style={[styles.tableCell, { flex: 1, alignItems: 'flex-end' }]}>
                            <Text style={styles.countText}>{topic.count}</Text>
                          </View>

                          <View style={[styles.tableCell, { flex: 2 }]}>
                            <View style={styles.progressRow}>
                              <View style={styles.progressTrack}>
                                <View style={[styles.progressBar2, { width: `${pct}%`, backgroundColor: color }]} />
                              </View>
                              <Text style={styles.percentText}>{pct}%</Text>
                            </View>
                          </View>
                        </View>
                      );
                    })}
                  </View>
                )}
              </View>
            )}
          </View>

          {/* ─── Funnel ─────────────────────────────────────── */}
          <View style={styles.section}>
            <SectionHeader
              title="ווריד המסע (Funnel)"
              desc="סשנים ייחודיים לכל שלב"
              sectionKey="funnel"
              icon={<Users size={15} color={COLORS.primary} strokeWidth={2} />}
            />

            {!collapsed.funnel && (
              <View style={styles.tableCard}>
                {filteredFunnel.length === 0 ? (
                  <View style={styles.emptyState}>
                    <Text style={styles.emptyText}>אין נתוני ווריד בטווח/בחיפוש הנבחר</Text>
                  </View>
                ) : (
                  <View style={styles.table}>
                    <View style={styles.tableHeader}>
                      <SortHeaderCell
                        flex={2}
                        label="שלב"
                        active={sortFunnelCfg.key === 'step'}
                        dir={sortFunnelCfg.dir}
                        onPress={() => setSortFunnelCfg((p) => ({ key: 'step', dir: p.key === 'step' ? (p.dir === 'asc' ? 'desc' : 'asc') : 'asc' }))}
                      />
                      <SortHeaderCell
                        flex={1}
                        label="סשנים"
                        active={sortFunnelCfg.key === 'count'}
                        dir={sortFunnelCfg.dir}
                        onPress={() => setSortFunnelCfg((p) => ({ key: 'count', dir: p.key === 'count' ? (p.dir === 'asc' ? 'desc' : 'asc') : 'desc' }))}
                        alignRight
                      />
                      <View style={[styles.tableHeaderCell, { flex: 3 }]} />
                    </View>

                    {filteredFunnel.map((item, idx) => {
                      const max = filteredFunnel[0]?.count || 1;
                      const pct = Math.round((item.count / max) * 100);
                      const funnelColors = [
                        COLORS.chart.blue,
                        COLORS.chart.purple,
                        COLORS.chart.cyan,
                        COLORS.chart.green,
                        COLORS.chart.lime,
                        COLORS.chart.orange,
                        COLORS.chart.pink,
                        COLORS.chart.yellow,
                        COLORS.chart.red,
                      ];
                      const color = funnelColors[idx % funnelColors.length];
                      const label = FUNNEL_STEP_LABELS[item.step] || item.step;

                      return (
                        <View key={item.step} style={styles.tableRow}>
                          <View style={[styles.tableCell, { flex: 2 }]}>
                            <View style={[styles.rankBadge, { backgroundColor: color + '22' }]}>
                              <Text style={[styles.rankNum, { color, fontSize: 11 }]}>{idx + 1}</Text>
                            </View>
                            <Text style={styles.topicText} numberOfLines={1}>
                              {label}
                            </Text>
                          </View>

                          <View style={[styles.tableCell, { flex: 1, alignItems: 'flex-end' }]}>
                            <Text style={styles.countText}>{item.count}</Text>
                          </View>

                          <View style={[styles.tableCell, { flex: 3 }]}>
                            <View style={styles.progressRow}>
                              <View style={styles.progressTrack}>
                                <View style={[styles.progressBar2, { width: `${pct}%`, backgroundColor: color }]} />
                              </View>
                              <Text style={styles.percentText}>{pct}%</Text>
                            </View>
                          </View>
                        </View>
                      );
                    })}
                  </View>
                )}
              </View>
            )}
          </View>

          {/* ─── Top clicks ─────────────────────────────────── */}
          <View style={styles.section}>
            <SectionHeader
              title="לחיצות מובילות"
              desc="Top 10"
              sectionKey="buttons"
              icon={<MousePointerClick size={15} color={COLORS.primary} strokeWidth={2} />}
            />

            {!collapsed.buttons && (
              <View style={styles.tableCard}>
                {filteredButtons.length === 0 ? (
                  <View style={styles.emptyState}>
                    <Text style={styles.emptyText}>אין נתוני לחיצות בטווח/בחיפוש הנבחר</Text>
                  </View>
                ) : (
                  <View style={styles.table}>
                    <View style={styles.tableHeader}>
                      <View style={[styles.tableHeaderCell, { flex: 0.4 }]}>
                        <Text style={styles.tableHeaderText}>#</Text>
                      </View>

                      <SortHeaderCell
                        flex={2.2}
                        label="כפתור"
                        active={sortButtonsCfg.key === 'button_name'}
                        dir={sortButtonsCfg.dir}
                        onPress={() =>
                          setSortButtonsCfg((p) => ({
                            key: 'button_name',
                            dir: p.key === 'button_name' ? (p.dir === 'asc' ? 'desc' : 'asc') : 'asc',
                          }))
                        }
                      />

                      <SortHeaderCell
                        flex={1.5}
                        label="עמוד"
                        active={sortButtonsCfg.key === 'page_path'}
                        dir={sortButtonsCfg.dir}
                        onPress={() =>
                          setSortButtonsCfg((p) => ({
                            key: 'page_path',
                            dir: p.key === 'page_path' ? (p.dir === 'asc' ? 'desc' : 'asc') : 'asc',
                          }))
                        }
                      />

                      <SortHeaderCell
                        flex={0.9}
                        label="לחיצות"
                        active={sortButtonsCfg.key === 'click_count'}
                        dir={sortButtonsCfg.dir}
                        onPress={() =>
                          setSortButtonsCfg((p) => ({
                            key: 'click_count',
                            dir: p.key === 'click_count' ? (p.dir === 'asc' ? 'desc' : 'asc') : 'desc',
                          }))
                        }
                        alignRight
                      />
                    </View>

                    {filteredButtons.map((btn, idx) => (
                      <View key={`${btn.button_name}_${idx}`} style={styles.tableRow}>
                        <View style={[styles.tableCell, { flex: 0.4 }]}>
                          <Text style={[styles.rankNum, { color: COLORS.text.tertiary }]}>{idx + 1}</Text>
                        </View>
                        <View style={[styles.tableCell, { flex: 2.2 }]}>
                          <Text style={styles.topicText} numberOfLines={1}>
                            {btn.button_name}
                          </Text>
                        </View>
                        <Pressable onPress={() => copyText(btn.page_path || '')} style={[styles.tableCell, { flex: 1.5 }]}>
                          <Text style={[styles.topicText, styles.linkText]} numberOfLines={1}>
                            {btn.page_path || '—'}
                          </Text>
                        </Pressable>
                        <View style={[styles.tableCell, { flex: 0.9, alignItems: 'flex-end' }]}>
                          <Text style={styles.countText}>{btn.click_count}</Text>
                        </View>
                      </View>
                    ))}
                  </View>
                )}
              </View>
            )}
          </View>

          {/* ─── Page engagement ─────────────────────────────── */}
          <View style={styles.section}>
            <SectionHeader
              title="מעורבות עמודים"
              desc="עומק גלילה וזמן שהייה"
              sectionKey="engagement"
              icon={<Clock size={15} color={COLORS.primary} strokeWidth={2} />}
            />

            {!collapsed.engagement && (
              <View style={styles.tableCard}>
                {filteredEng.length === 0 ? (
                  <View style={styles.emptyState}>
                    <Text style={styles.emptyText}>אין נתוני מעורבות בטווח/בחיפוש הנבחר</Text>
                  </View>
                ) : (
                  <View style={styles.table}>
                    <View style={styles.tableHeader}>
                      <SortHeaderCell
                        flex={2.5}
                        label="עמוד"
                        active={sortEngCfg.key === 'page_path'}
                        dir={sortEngCfg.dir}
                        onPress={() =>
                          setSortEngCfg((p) => ({
                            key: 'page_path',
                            dir: p.key === 'page_path' ? (p.dir === 'asc' ? 'desc' : 'asc') : 'asc',
                          }))
                        }
                      />
                      <SortHeaderCell
                        flex={0.9}
                        label="סשנים"
                        active={sortEngCfg.key === 'unique_sessions'}
                        dir={sortEngCfg.dir}
                        onPress={() =>
                          setSortEngCfg((p) => ({
                            key: 'unique_sessions',
                            dir: p.key === 'unique_sessions' ? (p.dir === 'asc' ? 'desc' : 'asc') : 'desc',
                          }))
                        }
                        alignRight
                      />
                      <SortHeaderCell
                        flex={0.9}
                        label="גלילה%"
                        active={sortEngCfg.key === 'avg_scroll_pct'}
                        dir={sortEngCfg.dir}
                        onPress={() =>
                          setSortEngCfg((p) => ({
                            key: 'avg_scroll_pct',
                            dir: p.key === 'avg_scroll_pct' ? (p.dir === 'asc' ? 'desc' : 'asc') : 'desc',
                          }))
                        }
                        alignRight
                      />
                      <SortHeaderCell
                        flex={1.2}
                        label="זמן ממוצע"
                        active={sortEngCfg.key === 'avg_time_seconds'}
                        dir={sortEngCfg.dir}
                        onPress={() =>
                          setSortEngCfg((p) => ({
                            key: 'avg_time_seconds',
                            dir: p.key === 'avg_time_seconds' ? (p.dir === 'asc' ? 'desc' : 'asc') : 'desc',
                          }))
                        }
                        alignRight
                      />
                    </View>

                    {filteredEng.map((row, idx) => {
                      const mins = Math.floor(row.avg_time_seconds / 60);
                      const secs = row.avg_time_seconds % 60;
                      const timeLabel = mins > 0 ? `${mins}:${secs.toString().padStart(2, '0')} דק׳` : `${secs} שנ׳`;
                      const scrollColor =
                        row.avg_scroll_pct >= 70 ? COLORS.success : row.avg_scroll_pct >= 40 ? COLORS.warning : COLORS.error;

                      return (
                        <View key={`${row.page_path}_${idx}`} style={styles.tableRow}>
                          <Pressable onPress={() => copyText(row.page_path)} style={[styles.tableCell, { flex: 2.5 }]}>
                            <Text style={[styles.topicText, styles.linkText]} numberOfLines={1}>
                              {row.page_path}
                            </Text>
                          </Pressable>
                          <View style={[styles.tableCell, { flex: 0.9, alignItems: 'flex-end' }]}>
                            <Text style={styles.countText}>{row.unique_sessions}</Text>
                          </View>
                          <View style={[styles.tableCell, { flex: 0.9, alignItems: 'flex-end' }]}>
                            <Text style={[styles.countText, { color: scrollColor }]}>{row.avg_scroll_pct}%</Text>
                          </View>
                          <View style={[styles.tableCell, { flex: 1.2, alignItems: 'flex-end' }]}>
                            <Text style={styles.countText}>{timeLabel}</Text>
                          </View>
                        </View>
                      );
                    })}
                  </View>
                )}
              </View>
            )}
          </View>

          {/* ─── College clicks ─────────────────────────────── */}
          <View style={styles.section}>
            <SectionHeader
              title="לחיצות על מכללות"
              desc='מכללה פופולרית של השבוע — סה"כ לחיצות לפי מכללה'
              sectionKey="college"
              icon={<MousePointerClick size={15} color={COLORS.primary} strokeWidth={2} />}
            />

            {!collapsed.college && (
              <View style={styles.tableCard}>
                {filteredCollegeClicks.length === 0 ? (
                  <View style={styles.emptyState}>
                    <Text style={styles.emptyText}>אין נתוני לחיצות על מכללות בטווח/בחיפוש הנבחר</Text>
                  </View>
                ) : (
                  <View style={styles.table}>
                    <View style={styles.tableHeader}>
                      <View style={[styles.tableHeaderCell, { flex: 0.4 }]}>
                        <Text style={styles.tableHeaderText}>#</Text>
                      </View>
                      <SortHeaderCell
                        flex={2}
                        label="מכללה"
                        active={sortCollegeCfg.key === 'college_name'}
                        dir={sortCollegeCfg.dir}
                        onPress={() =>
                          setSortCollegeCfg((p) => ({
                            key: 'college_name',
                            dir: p.key === 'college_name' ? (p.dir === 'asc' ? 'desc' : 'asc') : 'asc',
                          }))
                        }
                      />
                      <SortHeaderCell
                        flex={1}
                        label="לחיצות"
                        active={sortCollegeCfg.key === 'click_count'}
                        dir={sortCollegeCfg.dir}
                        onPress={() =>
                          setSortCollegeCfg((p) => ({
                            key: 'click_count',
                            dir: p.key === 'click_count' ? (p.dir === 'asc' ? 'desc' : 'asc') : 'desc',
                          }))
                        }
                        alignRight
                      />
                      <SortHeaderCell
                        flex={1.8}
                        label="לחיצה אחרונה"
                        active={sortCollegeCfg.key === 'last_clicked_at'}
                        dir={sortCollegeCfg.dir}
                        onPress={() =>
                          setSortCollegeCfg((p) => ({
                            key: 'last_clicked_at',
                            dir: p.key === 'last_clicked_at' ? (p.dir === 'asc' ? 'desc' : 'asc') : 'desc',
                          }))
                        }
                        alignRight
                      />
                      <View style={[styles.tableHeaderCell, { flex: 1.5 }]} />
                    </View>

                    {filteredCollegeClicks.map((row, idx) => {
                      const max = filteredCollegeClicks[0]?.click_count || 1;
                      const pct = Math.round((row.click_count / max) * 100);
                      const colors = [COLORS.chart.blue, COLORS.chart.purple, COLORS.chart.green, COLORS.chart.orange, COLORS.chart.pink];
                      const color = colors[idx % colors.length];
                      let lastLabel = '—';
                      try {
                        lastLabel = new Date(row.last_clicked_at).toLocaleString('he-IL', {
                          day: '2-digit',
                          month: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: false,
                        });
                      } catch {}

                      return (
                        <View key={`${row.college_name}_${idx}`} style={styles.tableRow}>
                          <View style={[styles.tableCell, { flex: 0.4 }]}>
                            <View style={[styles.rankBadge, { backgroundColor: color + '20' }]}>
                              <Text style={[styles.rankNum, { color }]}>{idx + 1}</Text>
                            </View>
                          </View>

                          <View style={[styles.tableCell, { flex: 2 }]}>
                            <Text style={styles.topicText} numberOfLines={1}>
                              {row.college_name}
                            </Text>
                          </View>

                          <View style={[styles.tableCell, { flex: 1, alignItems: 'flex-end' }]}>
                            <Text style={styles.countText}>{row.click_count}</Text>
                          </View>

                          <View style={[styles.tableCell, { flex: 1.8, alignItems: 'flex-end' }]}>
                            <Text style={[styles.countText, { fontSize: 11, color: COLORS.text.secondary }]}>{lastLabel}</Text>
                          </View>

                          <View style={[styles.tableCell, { flex: 1.5 }]}>
                            <View style={styles.progressRow}>
                              <View style={styles.progressTrack}>
                                <View style={[styles.progressBar2, { width: `${pct}%`, backgroundColor: color }]} />
                              </View>
                              <Text style={styles.percentText}>{pct}%</Text>
                            </View>
                          </View>
                        </View>
                      );
                    })}
                  </View>
                )}
              </View>
            )}
          </View>

          {/* ─── Topic clicks ───────────────────────────────── */}
          <View style={styles.section}>
            <SectionHeader
              title="לחיצות על תחומים"
              desc='דף התחומים (/fields) — כמה פעמים לחצו על כל תחום'
              sectionKey="topicClicks"
              icon={<MousePointerClick size={15} color={COLORS.primary} strokeWidth={2} />}
            />

            {!collapsed.topicClicks && (
              <View style={styles.tableCard}>
                {filteredTopicClicksData.length === 0 ? (
                  <View style={styles.emptyState}>
                    <Text style={styles.emptyText}>אין נתוני לחיצות על תחומים בטווח/בחיפוש הנבחר</Text>
                  </View>
                ) : (
                  <View style={styles.table}>
                    <View style={styles.tableHeader}>
                      <View style={[styles.tableHeaderCell, { flex: 0.4 }]}>
                        <Text style={styles.tableHeaderText}>#</Text>
                      </View>
                      <SortHeaderCell
                        flex={2}
                        label="תחום"
                        active={sortTopicClicksCfg.key === 'topic'}
                        dir={sortTopicClicksCfg.dir}
                        onPress={() =>
                          setSortTopicClicksCfg((p) => ({
                            key: 'topic',
                            dir: p.key === 'topic' ? (p.dir === 'asc' ? 'desc' : 'asc') : 'asc',
                          }))
                        }
                      />
                      <SortHeaderCell
                        flex={0.9}
                        label="לחיצות"
                        active={sortTopicClicksCfg.key === 'click_count'}
                        dir={sortTopicClicksCfg.dir}
                        onPress={() =>
                          setSortTopicClicksCfg((p) => ({
                            key: 'click_count',
                            dir: p.key === 'click_count' ? (p.dir === 'asc' ? 'desc' : 'asc') : 'desc',
                          }))
                        }
                        alignRight
                      />
                      <SortHeaderCell
                        flex={1.8}
                        label="לחיצה אחרונה"
                        active={sortTopicClicksCfg.key === 'last_clicked_at'}
                        dir={sortTopicClicksCfg.dir}
                        onPress={() =>
                          setSortTopicClicksCfg((p) => ({
                            key: 'last_clicked_at',
                            dir: p.key === 'last_clicked_at' ? (p.dir === 'asc' ? 'desc' : 'asc') : 'desc',
                          }))
                        }
                        alignRight
                      />
                      <View style={[styles.tableHeaderCell, { flex: 1.5 }]} />
                    </View>

                    {filteredTopicClicksData.map((row, idx) => {
                      const max = filteredTopicClicksData[0]?.click_count || 1;
                      const pct = Math.round((row.click_count / max) * 100);
                      const colors = [COLORS.chart.blue, COLORS.chart.purple, COLORS.chart.green, COLORS.chart.orange, COLORS.chart.pink, COLORS.chart.cyan, COLORS.chart.lime, COLORS.chart.yellow, COLORS.chart.red];
                      const color = colors[idx % colors.length];
                      let lastLabel = '—';
                      try {
                        lastLabel = new Date(row.last_clicked_at).toLocaleString('he-IL', {
                          day: '2-digit',
                          month: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: false,
                        });
                      } catch {}

                      return (
                        <View key={`${row.topic}_${idx}`} style={styles.tableRow}>
                          <View style={[styles.tableCell, { flex: 0.4 }]}>
                            <View style={[styles.rankBadge, { backgroundColor: color + '20' }]}>
                              <Text style={[styles.rankNum, { color }]}>{idx + 1}</Text>
                            </View>
                          </View>

                          <View style={[styles.tableCell, { flex: 2 }]}>
                            <Text style={styles.topicText} numberOfLines={1}>
                              {row.topic}
                            </Text>
                          </View>

                          <View style={[styles.tableCell, { flex: 0.9, alignItems: 'flex-end' }]}>
                            <Text style={styles.countText}>{row.click_count}</Text>
                          </View>

                          <View style={[styles.tableCell, { flex: 1.8, alignItems: 'flex-end' }]}>
                            <Text style={[styles.countText, { fontSize: 11, color: COLORS.text.secondary }]}>{lastLabel}</Text>
                          </View>

                          <View style={[styles.tableCell, { flex: 1.5 }]}>
                            <View style={styles.progressRow}>
                              <View style={styles.progressTrack}>
                                <View style={[styles.progressBar2, { width: `${pct}%`, backgroundColor: color }]} />
                              </View>
                              <Text style={styles.percentText}>{pct}%</Text>
                            </View>
                          </View>
                        </View>
                      );
                    })}
                  </View>
                )}
              </View>
            )}
          </View>

          {/* ─── Quick actions ──────────────────────────────── */}
          <View style={styles.section}>
            <SectionHeader
              title="פעולות מהירות"
              desc="קיצורי דרך"
              sectionKey="actions"
              icon={<Zap size={15} color={COLORS.primary} strokeWidth={2} />}
            />

            {!collapsed.actions && (
              <View style={styles.actionsGrid}>
                <Pressable onPress={() => router.push('/admin/export')} style={({ pressed }) => [styles.actionCard, pressed && { opacity: 0.95 }]}>
                  <View style={[styles.actionIcon, { backgroundColor: '#E0E7FF' }]}>
                    <Download size={18} color={COLORS.chart.purple} strokeWidth={2} />
                  </View>
                  <View style={styles.actionText}>
                    <Text style={styles.actionTitle}>ייצוא נתונים</Text>
                    <Text style={styles.actionDesc}>JSON ,Excel ועוד</Text>
                  </View>
                  <ChevronRight size={16} color={COLORS.text.tertiary} strokeWidth={2} />
                </Pressable>

                <Pressable onPress={() => router.push('/admin')} style={({ pressed }) => [styles.actionCard, pressed && { opacity: 0.95 }]}>
                  <View style={[styles.actionIcon, { backgroundColor: COLORS.successLight }]}>
                    <BookOpen size={18} color={COLORS.chart.green} strokeWidth={2} />
                  </View>
                  <View style={styles.actionText}>
                    <Text style={styles.actionTitle}>בחירות קורסים</Text>
                    <Text style={styles.actionDesc}>ניהול וצפייה בלידים</Text>
                  </View>
                  <ChevronRight size={16} color={COLORS.text.tertiary} strokeWidth={2} />
                </Pressable>

                <Pressable onPress={() => router.push('/admin')} style={({ pressed }) => [styles.actionCard, pressed && { opacity: 0.95 }]}>
                  <View style={[styles.actionIcon, { backgroundColor: COLORS.primaryBg }]}>
                    <MessageSquare size={18} color={COLORS.primary} strokeWidth={2} />
                  </View>
                  <View style={styles.actionText}>
                    <Text style={styles.actionTitle}>רשימת שיחות</Text>
                    <Text style={styles.actionDesc}>צפייה בכל השיחות והפרטים</Text>
                  </View>
                  <ChevronRight size={16} color={COLORS.text.tertiary} strokeWidth={2} />
                </Pressable>
              </View>
            )}
          </View>

          <View style={{ height: SPACING.huge }} />
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  page: { flex: 1, backgroundColor: COLORS.bg },

  loadingContainer: {
    flex: 1,
    backgroundColor: COLORS.bg,
    alignItems: 'center',
    justifyContent: 'center',
    gap: SPACING.lg,
  },
  loadingText: { ...TYPOGRAPHY.body, color: COLORS.text.secondary },

  navbar: {
    backgroundColor: COLORS.surface,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.border,
    ...SHADOW,
  },
  navContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: SPACING.xxl,
    paddingVertical: SPACING.lg,
    gap: SPACING.lg,
    flexWrap: 'wrap',
  },
  navLeft: { flexDirection: 'row', alignItems: 'center', gap: SPACING.sm },
  searchBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
    paddingHorizontal: SPACING.lg,
    paddingVertical: SPACING.sm,
    backgroundColor: COLORS.bg,
    borderRadius: RADIUS.md,
    borderWidth: 1,
    borderColor: COLORS.border,
    flex: 1,
    maxWidth: 420,
  },
  searchInput: {
    flex: 1,
    ...TYPOGRAPHY.body,
    color: COLORS.text.primary,
    ...(Platform.OS === 'web' ? { outlineStyle: 'none' as any } : {}),
  },
  clearBtn: {
    width: 28,
    height: 28,
    borderRadius: 0,
    backgroundColor: COLORS.borderLight,
    alignItems: 'center',
    justifyContent: 'center',
  },
  clearBtnText: { fontSize: 18, lineHeight: 20, color: COLORS.text.secondary, fontWeight: '600' as const },

  navRight: { flexDirection: 'row', alignItems: 'center', gap: SPACING.md },
  logoBox: {
    width: 38,
    height: 38,
    borderRadius: RADIUS.lg,
    backgroundColor: COLORS.primaryLight,
    alignItems: 'center',
    justifyContent: 'center',
  },
  navTitle: { ...TYPOGRAPHY.h4, color: COLORS.text.primary },
  navSubtitle: { ...TYPOGRAPHY.small, color: COLORS.text.secondary },

  iconBtn: {
    width: 38,
    height: 38,
    borderRadius: RADIUS.md,
    backgroundColor: COLORS.bg,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: COLORS.border,
  },
  primaryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs,
    paddingHorizontal: SPACING.lg,
    paddingVertical: SPACING.sm + 2,
    borderRadius: RADIUS.md,
    backgroundColor: COLORS.primary,
  },
  primaryBtnText: { ...TYPOGRAPHY.bodyMedium, color: '#FFF', fontWeight: '500' },

  scrollView: { flex: 1 },
  scrollContent: { paddingBottom: SPACING.xxxl },

  container: {
    width: '100%',
    maxWidth: 1400,
    marginHorizontal: 'auto' as any,
    paddingHorizontal: SPACING.xxl,
    paddingTop: SPACING.xl,
    gap: SPACING.xxl,
  },

  statusBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: SPACING.lg,
    paddingVertical: SPACING.md,
    backgroundColor: COLORS.surface,
    borderRadius: RADIUS.lg,
    borderWidth: 1,
    borderColor: COLORS.border,
    ...SHADOW,
    gap: SPACING.md,
    flexWrap: 'wrap',
  },
  dateRangeBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs,
    paddingHorizontal: SPACING.md,
    paddingVertical: 6,
    backgroundColor: COLORS.primaryLight,
    borderRadius: RADIUS.full,
  },
  dateRangeText: { ...TYPOGRAPHY.small, color: COLORS.primary, fontWeight: '500' },
  statusRight: { flexDirection: 'row', alignItems: 'center', gap: SPACING.sm },
  statusLabel: { ...TYPOGRAPHY.small, color: COLORS.text.tertiary },
  statusTime: { ...TYPOGRAPHY.small, color: COLORS.text.secondary, fontWeight: '500' },

  liveBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs,
    paddingHorizontal: SPACING.sm,
    paddingVertical: 4,
    backgroundColor: COLORS.successLight,
    borderRadius: RADIUS.full,
  },
  liveDot: { width: 6, height: 6, borderRadius: 3, backgroundColor: COLORS.success },
  liveText: { ...TYPOGRAPHY.small, color: COLORS.success, fontWeight: '500' },

  errorBanner: {
    padding: SPACING.lg,
    borderRadius: RADIUS.lg,
    backgroundColor: COLORS.errorLight,
    borderWidth: 1,
    borderColor: COLORS.error + '22',
  },
  errorBannerTitle: { ...TYPOGRAPHY.bodyMedium, color: COLORS.error, fontWeight: '700' },
  errorBannerText: { ...TYPOGRAPHY.body, color: COLORS.text.primary, marginTop: 4 },
  errorBannerHint: { ...TYPOGRAPHY.small, color: COLORS.text.secondary, marginTop: 6 },

  quickStats: { flexDirection: 'row', flexWrap: 'wrap', gap: SPACING.lg },
  quickCard: {
    flex: 1,
    minWidth: Platform.OS === 'web' ? 240 : '100%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.lg,
    padding: SPACING.xl,
    backgroundColor: COLORS.surface,
    borderRadius: RADIUS.xl,
    borderWidth: 1,
    borderColor: COLORS.border,
    ...SHADOW,
  },
  quickCardContent: { flex: 1, gap: SPACING.xs },
  quickLabel: { ...TYPOGRAPHY.caption, color: COLORS.text.secondary },
  quickValue: { ...TYPOGRAPHY.h1, fontSize: 28, color: COLORS.text.primary, fontWeight: '600' },
  quickSub: { ...TYPOGRAPHY.small, color: COLORS.text.tertiary, marginTop: 2 },
  quickTrend: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    alignSelf: 'flex-start',
    paddingHorizontal: SPACING.sm,
    paddingVertical: 3,
    borderRadius: RADIUS.sm,
  },
  quickTrendText: { ...TYPOGRAPHY.small, fontWeight: '600' },
  quickIcon: { width: 44, height: 44, borderRadius: RADIUS.lg, alignItems: 'center', justifyContent: 'center' },

  miniProgress: { height: 6, backgroundColor: COLORS.borderLight, borderRadius: 3, overflow: 'hidden' },
  miniProgressFill: { height: '100%', backgroundColor: COLORS.chart.purple, borderRadius: 3 },

  section: { gap: SPACING.lg },

  sectionHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: SPACING.md,
  },
  sectionHeaderLeft: { flexDirection: 'row', alignItems: 'center', gap: SPACING.sm, flex: 1 },
  sectionTitle: { ...TYPOGRAPHY.h3, color: COLORS.text.primary },
  sectionDescInline: { ...TYPOGRAPHY.caption, color: COLORS.text.secondary },

  collapsePill: {
    paddingHorizontal: SPACING.md,
    paddingVertical: 6,
    borderRadius: RADIUS.full,
    backgroundColor: COLORS.primaryBg,
    borderWidth: 1,
    borderColor: COLORS.primary + '22',
  },
  collapsePillClosed: { backgroundColor: COLORS.bg, borderColor: COLORS.border },
  collapsePillText: { ...TYPOGRAPHY.small, color: COLORS.primary, fontWeight: '700' },
  collapsePillTextClosed: { color: COLORS.text.secondary },

  card: {
    padding: SPACING.xl,
    backgroundColor: COLORS.surface,
    borderRadius: RADIUS.xl,
    borderWidth: 1,
    borderColor: COLORS.border,
    ...SHADOW,
    gap: SPACING.lg,
  },

  chipsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: SPACING.sm },
  chip: {
    paddingHorizontal: SPACING.lg,
    paddingVertical: SPACING.sm + 2,
    borderRadius: RADIUS.full,
    backgroundColor: COLORS.bg,
    borderWidth: 1,
    borderColor: COLORS.border,
  },
  chipActive: { backgroundColor: COLORS.primary, borderColor: COLORS.primary },
  chipText: { ...TYPOGRAPHY.bodyMedium, color: COLORS.text.secondary, fontWeight: '500' },
  chipTextActive: { color: '#FFF', fontWeight: '600' },

  customDateBox: { paddingTop: SPACING.lg, borderTopWidth: 1, borderTopColor: COLORS.border },
  dateRow: {
    flexDirection: Platform.OS === 'web' ? 'row' : 'column',
    gap: SPACING.md,
    alignItems: Platform.OS === 'web' ? 'flex-end' : 'stretch',
  },
  dateField: { flex: 1, gap: SPACING.xs },
  fieldLabel: { ...TYPOGRAPHY.caption, color: COLORS.text.secondary, fontWeight: '500' },
  dateInput: {
    paddingHorizontal: SPACING.lg,
    paddingVertical: SPACING.md,
    backgroundColor: COLORS.bg,
    borderRadius: RADIUS.md,
    borderWidth: 1,
    borderColor: COLORS.border,
    color: COLORS.text.primary,
    ...TYPOGRAPHY.body,
    ...(Platform.OS === 'web' ? { outlineStyle: 'none' as any } : {}),
  },
  applyBtn: {
    paddingHorizontal: SPACING.xl,
    paddingVertical: SPACING.md,
    borderRadius: RADIUS.md,
    backgroundColor: COLORS.primary,
  },
  applyBtnText: { ...TYPOGRAPHY.bodyMedium, color: '#FFF', fontWeight: '600' },

  inlineHintBox: {
    marginTop: SPACING.md,
    padding: SPACING.md,
    backgroundColor: COLORS.warningLight,
    borderRadius: RADIUS.md,
    borderWidth: 1,
    borderColor: COLORS.warning + '33',
  },
  inlineHintText: { ...TYPOGRAPHY.small, color: COLORS.text.primary },

  metricsGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: SPACING.lg },
  metricCard: {
    flex: 1,
    minWidth: Platform.OS === 'web' ? 300 : '100%',
    padding: SPACING.xl,
    backgroundColor: COLORS.surface,
    borderRadius: RADIUS.xl,
    borderWidth: 1,
    borderColor: COLORS.border,
    ...SHADOW,
    gap: SPACING.lg,
  },
  metricHeader: { flexDirection: 'row', alignItems: 'center', gap: SPACING.md },
  metricIconBox: { width: 38, height: 38, borderRadius: RADIUS.lg, alignItems: 'center', justifyContent: 'center' },
  metricTitle: { ...TYPOGRAPHY.h4, color: COLORS.text.primary },
  metricSubtitle: { ...TYPOGRAPHY.small, color: COLORS.text.secondary },
  metricValue: { ...TYPOGRAPHY.display, color: COLORS.text.primary },
  metricFooter: { gap: SPACING.sm },
  progressBar: { height: 8, backgroundColor: COLORS.borderLight, borderRadius: RADIUS.sm, overflow: 'hidden' },
  progressFill: { height: '100%', backgroundColor: COLORS.chart.purple, borderRadius: RADIUS.sm },
  trendBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    alignSelf: 'flex-start',
    paddingHorizontal: SPACING.sm + 2,
    paddingVertical: 5,
    borderRadius: RADIUS.full,
  },
  trendText: { ...TYPOGRAPHY.caption, fontWeight: '600' },
  metricNote: { ...TYPOGRAPHY.caption, color: COLORS.text.tertiary },

  totalCard: {
    padding: SPACING.xxl,
    backgroundColor: COLORS.surface,
    borderRadius: RADIUS.xl,
    borderWidth: 1,
    borderColor: COLORS.border,
    ...SHADOW,
    gap: SPACING.lg,
    alignItems: 'center',
  },
  totalIcon: { width: 56, height: 56, borderRadius: RADIUS.xl, alignItems: 'center', justifyContent: 'center' },
  totalValue: { ...TYPOGRAPHY.display, color: COLORS.text.primary },
  totalBreakdown: { flexDirection: 'row', alignItems: 'center', gap: SPACING.lg },
  breakdownItem: { alignItems: 'center', gap: 4 },
  breakdownValue: { ...TYPOGRAPHY.h3, color: COLORS.text.primary },
  breakdownLabel: { ...TYPOGRAPHY.small, color: COLORS.text.tertiary },
  breakdownDivider: { width: 1, height: 32, backgroundColor: COLORS.border },

  chartsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: SPACING.lg },
  chartCard: {
    flex: 1,
    minWidth: Platform.OS === 'web' ? 400 : '100%',
    padding: SPACING.xl,
    backgroundColor: COLORS.surface,
    borderRadius: RADIUS.xl,
    borderWidth: 1,
    borderColor: COLORS.border,
    ...SHADOW,
    gap: SPACING.lg,
  },
  chartHeader: { flexDirection: 'row', alignItems: 'flex-start', gap: SPACING.md },
  chartIconBox: { width: 34, height: 34, borderRadius: RADIUS.md, alignItems: 'center', justifyContent: 'center' },
  chartTitle: { ...TYPOGRAPHY.h4, color: COLORS.text.primary },
  chartSubtitle: { ...TYPOGRAPHY.caption, color: COLORS.text.secondary },
  chartBody: { alignItems: 'center', justifyContent: 'center', paddingVertical: SPACING.lg },

  tableCard: {
    backgroundColor: COLORS.surface,
    borderRadius: RADIUS.xl,
    borderWidth: 1,
    borderColor: COLORS.border,
    ...SHADOW,
    overflow: 'hidden',
  },
  table: { gap: 1, backgroundColor: COLORS.border },
  tableHeader: { flexDirection: 'row', paddingHorizontal: SPACING.xl, paddingVertical: SPACING.md, backgroundColor: COLORS.bg },
  tableHeaderCell: { flexDirection: 'row', alignItems: 'center' },
  tableHeaderText: { ...TYPOGRAPHY.small, color: COLORS.text.tertiary, fontWeight: '600', textTransform: 'uppercase' as any },

  tableRow: { flexDirection: 'row', paddingHorizontal: SPACING.xl, paddingVertical: SPACING.lg, backgroundColor: COLORS.surface },
  tableCell: { flexDirection: 'row', alignItems: 'center', gap: SPACING.sm },
  rankBadge: { width: 28, height: 28, borderRadius: RADIUS.md, alignItems: 'center', justifyContent: 'center' },
  rankNum: { ...TYPOGRAPHY.bodyMedium, fontWeight: '700' },
  topicText: { flex: 1, ...TYPOGRAPHY.bodyMedium, color: COLORS.text.primary },
  linkText: { textDecorationLine: Platform.OS === 'web' ? ('underline' as any) : ('none' as any), color: COLORS.primary },

  countText: { ...TYPOGRAPHY.h4, color: COLORS.text.primary },
  progressRow: { flexDirection: 'row', alignItems: 'center', gap: SPACING.md },
  progressTrack: { flex: 1, height: 8, backgroundColor: COLORS.borderLight, borderRadius: RADIUS.sm, overflow: 'hidden' },
  progressBar2: { height: '100%', borderRadius: RADIUS.sm },
  percentText: { ...TYPOGRAPHY.caption, color: COLORS.text.secondary, fontWeight: '500', minWidth: 38, textAlign: 'right' },

  emptyState: { paddingVertical: SPACING.xxxl, alignItems: 'center' },
  emptyText: { ...TYPOGRAPHY.body, color: COLORS.text.tertiary },

  actionsGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: SPACING.lg },
  actionCard: {
    flex: 1,
    minWidth: Platform.OS === 'web' ? 320 : '100%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.lg,
    padding: SPACING.xl,
    backgroundColor: COLORS.surface,
    borderRadius: RADIUS.xl,
    borderWidth: 1,
    borderColor: COLORS.border,
    ...SHADOW,
  },
  actionIcon: { width: 48, height: 48, borderRadius: RADIUS.lg, alignItems: 'center', justifyContent: 'center' },
  actionText: { flex: 1, gap: SPACING.xs },
  actionTitle: { ...TYPOGRAPHY.h4, color: COLORS.text.primary },
  actionDesc: { ...TYPOGRAPHY.caption, color: COLORS.text.secondary },
});